import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, HelpCircle } from "lucide-react";

const faqs = [
  {
    q: "Combien coûte une certification ISO 27001 ?",
    a: "Le coût total d'une certification ISO 27001 dépend de la taille de votre périmètre et de votre maturité initiale. Comptez généralement entre 25 k€ et 80 k€ pour une PME / ETI, incluant l'accompagnement BaobSec et les frais d'organisme certificateur. Nous établissons un devis précis après un diagnostic gratuit de 1h.",
  },
  {
    q: "En combien de temps peut-on être certifié ISO 27001 ?",
    a: "Notre méthode standard amène une organisation à la certification en 6 à 12 mois selon la complexité. Pour une PME structurée, 9 mois est le délai cible : 2 mois de diagnostic & SMSI, 4 mois d'implémentation, 2 mois d'audit interne, 1 mois pour l'audit de certification.",
  },
  {
    q: "Faut-il une équipe interne pour avoir un SOC ?",
    a: "Non. Nous proposons un SOC managé 24/7 sans recrutement de votre côté : nos analystes surveillent vos systèmes, qualifient les alertes et coordonnent la réponse à incident. Vous gardez la main sur les décisions critiques via un canal dédié.",
  },
  {
    q: "Mon entreprise est-elle concernée par NIS 2 ?",
    a: "Si vous êtes une moyenne ou grande entreprise (>50 personnes ou >10 M€ CA) opérant dans un des 18 secteurs essentiels ou importants (énergie, santé, finance, transport, services numériques, etc.), oui — vous devez être en conformité. Nous offrons un diagnostic NIS 2 express en 5 jours.",
  },
  {
    q: "Intervenez-vous hors de France et du Sénégal ?",
    a: "Oui. Notre double implantation France 🇫🇷 / Sénégal 🇸🇳 nous permet de servir des clients partout en Europe et en Afrique de l'Ouest. Nous intervenons aussi à distance avec une qualité de livraison identique : nos consultants, missions et runbooks sont structurés pour le remote-first.",
  },
  {
    q: "Quelle est la différence entre EBIOS RM et ISO 27005 ?",
    a: "EBIOS RM est la méthode française d'analyse de risques publiée par l'ANSSI, orientée scénarios d'attaque et parties prenantes. ISO/IEC 27005 est la norme internationale, plus générique. Les deux sont compatibles — nous utilisons EBIOS RM en France et au Sénégal, et ISO 27005 pour des contextes internationaux multi-sites.",
  },
  {
    q: "Quel est le délai pour réagir à un incident de sécurité ?",
    a: "Pour nos clients en SOC managé, le temps de détection moyen est inférieur à 5 minutes et le temps de qualification & escalade à 30 minutes (MTTR < 30 min). Pour la réponse à incident en mode urgence (CSIRT), nous sommes mobilisables sous 4h ouvrées via notre hotline dédiée.",
  },
  {
    q: "Travaillez-vous avec des startups / fintechs en phase de levée ?",
    a: "Oui, c'est même un de nos secteurs forts. Nous accompagnons les fintechs et SaaS B2B pour passer les due-diligences sécurité des investisseurs et grands comptes : SOC 2 readiness, ISO 27001 fast-track, politique de sécurité 'investor-ready' en 4 à 6 semaines.",
  },
];

function FAQItem({ item, idx, open, onToggle }) {
  return (
    <div
      data-testid={`faq-item-${idx}`}
      className={`border-b border-[#1A2E22] transition-colors ${
        open ? "bg-[#0C1410]" : ""
      }`}
    >
      <button
        onClick={onToggle}
        data-testid={`faq-toggle-${idx}`}
        className="w-full flex items-start justify-between gap-6 py-6 text-left group"
      >
        <div className="flex items-start gap-4">
          <span className="font-mono text-xs text-emerald-400 pt-1.5">
            0{idx + 1}
          </span>
          <span className="font-display text-lg md:text-xl font-medium text-white group-hover:text-emerald-400 transition-colors">
            {item.q}
          </span>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-emerald-400 shrink-0 mt-2 transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="pb-6 pl-10 pr-12 text-gray-400 leading-relaxed">
              {item.a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQSection() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section id="faq" className="relative py-24 md:py-32 bg-[#080F0B] border-y border-white/5">
      <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-7">
            <div className="font-mono text-xs text-emerald-400 mb-4 tracking-widest">
              // QUESTIONS FRÉQUENTES
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-semibold leading-[1.05] tracking-tight">
              Les bonnes réponses
              <br />
              <span className="italic text-emerald-400">aux vraies questions.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <div className="inline-flex items-start gap-3 p-4 rounded-2xl bg-emerald-500/5 border border-emerald-500/20">
              <HelpCircle className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
              <div className="text-sm text-gray-300">
                Votre question n'y figure pas ?{" "}
                <a
                  href="#contact"
                  className="text-emerald-400 underline underline-offset-4 hover:text-emerald-300"
                >
                  Posez-la directement
                </a>{" "}
                — nous répondons sous 24h.
              </div>
            </div>
          </div>
        </div>

        <div data-testid="faq-list" className="border-t border-[#1A2E22]">
          {faqs.map((f, i) => (
            <FAQItem
              key={i}
              item={f}
              idx={i}
              open={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
